/**
 * Morph Git Client - Simple, high-level Git operations
 * Built on isomorphic-git with explicit configuration
 */


import git from 'isomorphic-git';
import http from 'isomorphic-git/http/node';
import fs from 'fs';
import type {
  CloneOptions,
  PushOptions,
  PullOptions,
  AddOptions,
  CommitOptions,
  StatusOptions,
  LogOptions,
  CheckoutOptions,
  BranchOptions,
  DiffOptions,
  CommitObject,
  StatusResult,
  MorphGitConfig,
  MorphNotesSchema,
  WaitForEmbeddingsOptions,
  EmbeddingProgress,
} from './types.js';
import { MorphAPIClient } from '../core/client.js';
import { APIResource } from '../core/resource.js';

const DEFAULT_PROXY_URL = 'https://repos.morphllm.com';

/**
 * MorphGit - Git operations for AI agents with Morph backend
 * 
 * @example
 * ```typescript
 * import { MorphGit } from 'morphsdk/git';
 * 
 * const morphGit = new MorphGit({
 *   apiKey: process.env.MORPH_API_KEY!,
 *   proxyUrl: 'https://repos.morphllm.com' // Optional
 * });
 * 
 * await morphGit.init({ repoId: 'my-project', dir: './my-project' });
 * await morphGit.push({ dir: './my-project' });
 * ```
 *
 * @deprecated Prefer the unified `MorphClient` (`new MorphClient({ apiKey }).git`).
 * Standalone clients remain only for backwards compatibility and may be removed in a future
 * major version — do not use them in new code.
 */
export class MorphGit extends APIResource {
  private readonly apiKey: string;
  private readonly proxyUrl: string;

  constructor(clientOrConfig: MorphAPIClient | MorphGitConfig) {
    const isClient = clientOrConfig instanceof MorphAPIClient;

    // Standalone construction validates the key eagerly (mirrors the historical
    // contract). When sharing a transport, MorphClient has already validated it.
    if (!isClient) {
      if (!clientOrConfig.apiKey) {
        throw new Error('API key is required. Get one at https://morphllm.com/dashboard');
      }
      if (!clientOrConfig.apiKey.startsWith('sk-') && !clientOrConfig.apiKey.startsWith('morph-')) {
        throw new Error('Invalid API key format. Expected: sk-... or morph-...');
      }
    }

    super(
      isClient
        ? clientOrConfig
        : new MorphAPIClient({
            apiKey: clientOrConfig.apiKey,
            reposURL: clientOrConfig.proxyUrl,
            retryConfig: clientOrConfig.retryConfig,
          }),
    );

    // Git is a distinct service (isomorphic-git + raw fetch to the repos host);
    // it keeps its own wire but sources the key/host from the shared transport.
    this.apiKey = this._client.resolveApiKey() ?? '';
    this.proxyUrl = isClient ? this._client.reposURL : (clientOrConfig.proxyUrl || DEFAULT_PROXY_URL);
  }
  
  /**
   * Get auth callback for isomorphic-git operations
   * @private
   */
  private getAuthCallback() {
    return () => ({
      username: 'morph',
      password: this.apiKey,
    });
  }

  /**
   * Initialize a new repository
   * Creates the repo in the database and in the git provider
   * 
   * @example
   * ```ts
   * await morphGit.init({
   *   repoId: 'my-project',
   *   dir: './my-project',
   *   defaultBranch: 'main'
   * });
   * ```
   */
  async init(options: {
    repoId: string;
    dir: string;
    defaultBranch?: string;
  }): Promise<void> {
    const { repoId, dir, defaultBranch = 'main' } = options;

    // Call backend API to create repository
    const response = await fetch(`${this.proxyUrl}/v1/repos`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        repoId,
        name: repoId,
        defaultBranch,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to create repository: ${error}`);
    }

    // Initialize local git repository (industry standard: no clone needed)
    await git.init({
      fs,
      dir,
      defaultBranch,
    });

    // Add remote pointing to Morph git-proxy
    await git.addRemote({
      fs,
      dir,
      remote: 'origin',
      url: `${this.proxyUrl}/v1/repos/${repoId}`,
    });

    console.log(`✓ Repository '${repoId}' initialized`);
  }

  /**
   * Clone a repository from Morph repos
   * 
   * @example
   * ```ts
   * await morphGit.clone({
   *   repoId: 'my-project',
   *   dir: './my-project'
   * });
   * ```
   */
  async clone(options: CloneOptions): Promise<void> {
    const { repoId, dir, branch = 'main', depth, singleBranch = true } = options;

    await git.clone({
      fs,
      http,
      dir,
      url: `${this.proxyUrl}/v1/repos/${repoId}`,
      ref: branch,
      singleBranch,
      depth,
      onAuth: this.getAuthCallback(),
    });
  }

  /**
   * Push changes to remote repository
   * 
   * @example
   * ```ts
   * await morphGit.push({ 
   *   dir: './my-project',
   *   branch: 'main', // Required: explicit branch name
   *   index: true     // Optional: generate embeddings for semantic search
   * });
   * ```
   */
  async push(options: PushOptions): Promise<void> {
    const { dir, remote = 'origin', branch, waitForEmbeddings, index = false } = options;

    if (!branch) {
      throw new Error(
        'branch is required for push operations. ' +
        'Specify the branch explicitly: { dir: "./my-project", branch: "main" }'
      );
    }

    // Get commit hash and repoId before pushing
    const commitHash = await git.resolveRef({ fs, dir, ref: 'HEAD' });
    
    // Get repoId from git remote URL
    let repoId: string | undefined;
    const remotes = await git.listRemotes({ fs, dir });
    const originRemote = remotes.find(r => r.remote === remote);
    if (originRemote) {
      // Extract repoId from URL: https://repos.morphllm.com/v1/repos/{repoId}
      const match = originRemote.url.match(/\/repos\/([^\/]+)$/);
      if (match) {
        repoId = match[1];
      }
    }

    await git.push({
      fs,
      http,
      dir,
      remote,
      ref: branch,
      onAuth: this.getAuthCallback(),
    });
    
    // Configure commit after successful push (set index flag)
    if (repoId && commitHash) {
      await this.configureCommit({ repoId, commitHash, branch, index });
    }
    
    // Wait for embeddings if requested (and indexing is enabled)
    if (waitForEmbeddings && repoId && commitHash && index) {
      await this.waitForEmbeddings({ repoId, commitHash });
    }
  }

  /**
   * Configure commit settings on the backend after push.
   * Sets the index flag to control embedding generation.
   * @private
   */
  private async configureCommit(options: {
    repoId: string;
    commitHash: string;
    branch: string;
    index: boolean;
  }): Promise<void> {
    const { repoId, commitHash, branch, index } = options;
    
    const response = await fetch(
      `${this.proxyUrl}/v1/repos/${repoId}/commits/${commitHash}/config`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ index, branch }),
      }
    );

    if (!response.ok) {
      // Non-fatal: log warning but don't throw
      console.warn(`Failed to configure commit: ${response.status}`);
    }
  }

  /**
   * Pull changes from remote repository
   * 
   * @example
   * ```ts
   * await morphGit.pull({ 
   *   dir: './my-project',
   *   branch: 'main' // Required: explicit branch name
   * });
   * ```
   */
  async pull(options: PullOptions): Promise<void> {
    const { dir, remote = 'origin', branch } = options;

    if (!branch) {
      throw new Error(
        'branch is required for pull operations. ' +
        'Specify the branch explicitly: { dir: "./my-project", branch: "main" }'
      );
    }

    await git.pull({
      fs,
      http,
      dir,
      remote,
      ref: branch,
      onAuth: this.getAuthCallback(),
      author: {
        name: 'Morph Agent',
        email: 'agent@morph.com',
      },
    });
  }

  /**
   * Wait for embeddings to complete after push.
   * Polls status endpoint until embeddings are done.
   * 
   * @example
   * ```ts
   * await morphGit.push({ dir: './my-project', branch: 'main' });
   * await morphGit.waitForEmbeddings({
   *   repoId: 'my-project',
   *   onProgress: (p) => console.log(`${p.filesProcessed}/${p.totalFiles}`)
   * });
   * ```
   */
  async waitForEmbeddings(options: WaitForEmbeddingsOptions): Promise<void> {
    const { repoId, commitHash, timeout = 120000, onProgress } = options;
    const startTime = Date.now();
    const pollInterval = 1000;  // Poll every 1s
    
    while (Date.now() - startTime < timeout) {
      const statusUrl = `${this.proxyUrl}/v1/repos/${repoId}/embedding-status` +
        (commitHash ? `?commit_hash=${commitHash}` : '');
      
      const response = await fetch(statusUrl, {
        headers: { 'Authorization': `Bearer ${this.apiKey}` }
      });
      
      if (response.status === 404) {
        // No job found yet - might still be creating
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        continue;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to get embedding status: ${response.status}`);
      }
      
      const status = await response.json();
      
      if (onProgress && status.progress) {
        onProgress(status.progress);
      }
      
      if (status.status === 'completed') {
        return;  // Done!
      }
      
      if (status.status === 'failed') {
        throw new Error(`Embeddings failed: ${status.error || 'Unknown error'}`);
      }
      
      // Still processing (queued or processing), wait and poll again
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
    
    throw new Error(`Embeddings timed out after ${timeout}ms`);
  }

  /**
   * Stage a file for commit
   * 
   * @example
   * ```ts
   * await morphGit.add({
   *   dir: './my-project',
   *   filepath: 'src/app.ts'
   * });
   * ```
   */
  async add(options: AddOptions): Promise<void> {
    const { dir, filepath } = options;

    await git.add({
      fs,
      dir,
      filepath,
    });
  }

  /**
   * Remove a file from staging
   * 
   * @example
   * ```ts
   * await morphGit.remove({
   *   dir: './my-project',
   *   filepath: 'src/old-file.ts'
   * });
   * ```
   */
  async remove(options: AddOptions): Promise<void> {
    const { dir, filepath } = options;

    await git.remove({
      fs,
      dir,
      filepath,
    });
  }

  /**
   * Commit staged changes
   * 
   * @example
   * ```ts
   * await morphGit.commit({
   *   dir: './my-project',
   *   message: 'Add new feature',
   *   author: {
   *     name: 'AI Agent',
   *     email: 'ai@example.com'
   *   },
   *   metadata: { issueId: 'PROJ-123', source: 'agent' },
   *   chatHistory: [
   *     { role: 'user', content: 'Please add a new feature' },
   *     { role: 'assistant', content: 'I will add that feature' }
   *   ],
   *   recordingId: 'rec_123'
   * });
   * ```
   */
  async commit(options: CommitOptions): Promise<string> {
    const { dir, message, author, metadata, chatHistory, recordingId } = options;

    // Provide default author if not specified
    const commitAuthor = author || {
      name: 'Morph SDK',
      email: 'sdk@morphllm.com'
    };

    const sha = await git.commit({
      fs,
      dir,
      message,
      author: commitAuthor,
    });

    // Store notes if any note fields are provided
    if (metadata || chatHistory || recordingId) {
      const notes: MorphNotesSchema = {
        metadata,
        chatHistory,
        recordingId,
        _version: 1
      };
      
      await git.addNote({
        fs,
        dir,
        ref: 'refs/notes/morph-metadata',
        oid: sha,
        note: JSON.stringify(notes, null, 2),
        author: commitAuthor
      });
    }

    return sha;
  }

  /**
   * Get status of a file
   * 
   * @example
   * ```ts
   * const status = await morphGit.status({
   *   dir: './my-project',
   *   filepath: 'src/app.ts'
   * });
   * console.log(status); // 'modified', '*added', etc.
   * ```
   */
  async status(options: StatusOptions): Promise<string> {
    const { dir, filepath } = options;

    if (!filepath) {
      throw new Error('filepath is required for status check');
    }

    const status = await git.status({
      fs,
      dir,
      filepath,
    });

    return status;
  }

  /**
   * Get commit history
   * 
   * @example
   * ```ts
   * const commits = await morphGit.log({
   *   dir: './my-project',
   *   depth: 10
   * });
   * ```
   */
  async log(options: LogOptions): Promise<CommitObject[]> {
    const { dir, depth, ref } = options;

    const commits = await git.log({
      fs,
      dir,
      depth,
      ref,
    });

    return commits as CommitObject[];
  }

  /**
   * Checkout a branch or commit
   * 
   * @example
   * ```ts
   * await morphGit.checkout({
   *   dir: './my-project',
   *   ref: 'feature-branch'
   * });
   * ```
   */
  async checkout(options: CheckoutOptions): Promise<void> {
    const { dir, ref } = options;

    await git.checkout({
      fs,
      dir,
      ref,
    });
  }

  /**
   * Create a new branch
   * 
   * @example
   * ```ts
   * await morphGit.branch({
   *   dir: './my-project',
   *   name: 'feature-branch',
   *   checkout: true
   * });
   * ```
   */
  async branch(options: BranchOptions): Promise<void> {
    const { dir, name, checkout = false } = options;

    await git.branch({
      fs,
      dir,
      ref: name,
      checkout,
    });
  }

  /**
   * List all branches
   * 
   * @example
   * ```ts
   * const branches = await morphGit.listBranches({
   *   dir: './my-project'
   * });
   * ```
   */
  async listBranches(options: { dir: string }): Promise<string[]> {
    const { dir } = options;

    const branches = await git.listBranches({
      fs,
      dir,
    });

    return branches;
  }

  /**
   * Get the current branch name
   * 
   * @example
   * ```ts
   * const branch = await morphGit.currentBranch({
   *   dir: './my-project'
   * });
   * ```
   */
  async currentBranch(options: { dir: string }): Promise<string | undefined> {
    const { dir } = options;

    const branch = await git.currentBranch({
      fs,
      dir,
    });

    return branch || undefined;
  }

  /**
   * Get list of changed files (similar to git diff --name-only)
   * 
   * @example
   * ```ts
   * const changes = await morphGit.statusMatrix({
   *   dir: './my-project'
   * });
   * ```
   */
  async statusMatrix(options: { dir: string }): Promise<StatusResult[]> {
    const { dir } = options;

    const matrix = await git.statusMatrix({
      fs,
      dir,
    });

    return matrix.map(([filepath, HEADStatus, workdirStatus, stageStatus]) => {
      let status: StatusResult['status'] = 'unmodified';

      // Determine status based on statusMatrix values
      if (HEADStatus === 1 && workdirStatus === 2 && stageStatus === 2) {
        status = 'modified';
      } else if (HEADStatus === 1 && workdirStatus === 2 && stageStatus === 1) {
        status = '*modified';
      } else if (HEADStatus === 0 && workdirStatus === 2 && stageStatus === 2) {
        status = 'added';
      } else if (HEADStatus === 0 && workdirStatus === 2 && stageStatus === 0) {
        status = '*added';
      } else if (HEADStatus === 1 && workdirStatus === 0 && stageStatus === 0) {
        status = 'deleted';
      } else if (HEADStatus === 1 && workdirStatus === 0 && stageStatus === 1) {
        status = '*deleted';
      } else if (HEADStatus === 1 && workdirStatus === 1 && stageStatus === 1) {
        status = 'unmodified';
      } else if (HEADStatus === 0 && workdirStatus === 0 && stageStatus === 0) {
        status = 'absent';
      }

      return {
        filepath,
        status,
      };
    });
  }

  /**
   * Get the current commit hash
   * 
   * @example
   * ```ts
   * const hash = await morphGit.resolveRef({
   *   dir: './my-project',
   *   ref: 'HEAD'
   * });
   * ```
   */
  async resolveRef(options: { dir: string; ref: string }): Promise<string> {
    const { dir, ref } = options;

    const oid = await git.resolveRef({
      fs,
      dir,
      ref,
    });

    return oid;
  }

  /**
   * Get notes (metadata, chat history, recording ID) attached to a commit
   * 
   * @example
   * ```ts
   * const notes = await morphGit.getCommitMetadata({
   *   dir: './my-project',
   *   commitSha: 'abc123...'
   * });
   * 
   * if (notes) {
   *   console.log('Metadata:', notes.metadata);
   *   console.log('Chat history:', notes.chatHistory);
   *   console.log('Recording ID:', notes.recordingId);
   * }
   * ```
   */
  async getCommitMetadata(options: {
    dir: string;
    commitSha: string;
  }): Promise<MorphNotesSchema | null> {
    try {
      const note = await git.readNote({
        fs,
        dir: options.dir,
        ref: 'refs/notes/morph-metadata',
        oid: options.commitSha
      });
      
      const notes: MorphNotesSchema = JSON.parse(new TextDecoder().decode(note));
      return notes;
    } catch (err) {
      // No notes found for this commit
      return null;
    }
  }
}

